
import React, { useState } from 'react';
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { CalendarIcon, Clock } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Form, FormField, FormItem, FormControl, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";

// Mise à jour des créneaux horaires selon les nouveaux horaires d'ouverture
const timeSlots = [
  "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
  "14:00", "14:30", "15:00", "15:30", "16:00", "16:30", "17:00", "17:30", "18:00", "18:30"
];

// Créneaux spécifiques pour le vendredi (9h-13h)
const fridayTimeSlots = [
  "09:00", "09:30", "10:00", "10:30", "11:00", "11:30", "12:00", "12:30"
];

interface AppointmentCalendarProps {
  onClose: () => void;
}

// Schéma de validation pour le formulaire
const appointmentSchema = z.object({
  firstName: z.string().min(1, "Le prénom est requis"),
  lastName: z.string().min(1, "Le nom est requis"),
  email: z.string().email("Email invalide").min(1, "L'email est requis"),
});

type AppointmentFormValues = z.infer<typeof appointmentSchema>;

const AppointmentCalendar: React.FC<AppointmentCalendarProps> = ({ onClose }) => {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const form = useForm<AppointmentFormValues>({
    resolver: zodResolver(appointmentSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
    }
  });

  const handleSubmit = async (formValues: AppointmentFormValues) => {
    if (!selectedDate || !selectedTime) {
      toast({
        title: "Information manquante",
        description: "Veuillez sélectionner une date et un horaire",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    // Formatage de la date et heure pour l'email
    const formattedDate = format(selectedDate, "d MMMM yyyy", { locale: fr });
    const appointmentData = {
      ...formValues,
      appointmentDate: formattedDate,
      appointmentTime: selectedTime,
      type: "Demande de rendez-vous",
    };

    try {
      // Envoi du formulaire via Formspree
      const response = await fetch("https://formspree.io/f/xrbpoweq", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(appointmentData),
      });

      if (response.ok) {
        toast({
          title: "Rendez-vous confirmé",
          description: `Votre rendez-vous est confirmé pour le ${formattedDate} à ${selectedTime}.`,
        });
        onClose();
      } else {
        throw new Error("Erreur lors de l'envoi du formulaire");
      }
    } catch (error) {
      console.error("Erreur lors de l'envoi:", error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue. Veuillez réessayer.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Disable weekends and past dates
  const isDateDisabled = (date: Date) => {
    const day = date.getDay();
    // Désactiver samedi (6) et dimanche (0)
    const isWeekend = day === 0 || day === 6;
    const isPastDate = date < new Date(new Date().setHours(0, 0, 0, 0));
    return isWeekend || isPastDate;
  };

  // Déterminer les créneaux horaires en fonction du jour de la semaine
  const getAvailableTimeSlots = () => {
    if (!selectedDate) return [];
    
    const day = selectedDate.getDay();
    // Si c'est vendredi (jour 5), utiliser les créneaux spécifiques du vendredi
    return day === 5 ? fridayTimeSlots : timeSlots;
  };

  return (
    <div className="flex flex-col space-y-2">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-2">
          <div className="flex flex-col items-center space-y-3">
            <div className="bg-gray-50 rounded-md p-2 w-full">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => {
                  setSelectedDate(date);
                  setSelectedTime(null); // Réinitialiser l'heure lors du changement de date
                }}
                disabled={isDateDisabled}
                className="p-0 pointer-events-auto"
              />
            </div>
            
            {selectedDate && (
              <div className="w-full">
                <div className="flex items-center mb-1 text-sm">
                  <CalendarIcon className="mr-1 h-3 w-3 text-patrimoine-gold" />
                  <span className="font-medium">
                    {format(selectedDate, "d MMMM yyyy", { locale: fr })}
                  </span>
                </div>
                <div className="grid grid-cols-4 gap-1 w-full">
                  {getAvailableTimeSlots().map((time) => (
                    <Button
                      key={time}
                      type="button"
                      variant={selectedTime === time ? "default" : "outline"}
                      className={`h-8 text-xs px-1 ${selectedTime === time ? 'bg-patrimoine-gold text-white hover:bg-amber-600' : ''}`}
                      onClick={() => setSelectedTime(time)}
                    >
                      <Clock className="mr-1 h-3 w-3" />
                      {time}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {selectedDate && selectedTime && (
              <div className="w-full space-y-2 border-t pt-2">
                <h3 className="font-medium text-sm text-center">Vos coordonnées</h3>
                
                <div className="grid grid-cols-2 gap-2">
                  <FormField
                    control={form.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem className="space-y-1">
                        <Label htmlFor="firstName" className="text-xs">Prénom</Label>
                        <FormControl>
                          <Input {...field} id="firstName" placeholder="Votre prénom" className="h-8 text-sm" />
                        </FormControl>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem className="space-y-1">
                        <Label htmlFor="lastName" className="text-xs">Nom</Label>
                        <FormControl>
                          <Input {...field} id="lastName" placeholder="Votre nom" className="h-8 text-sm" />
                        </FormControl>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />
                </div>
                
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem className="space-y-1">
                      <Label htmlFor="email" className="text-xs">Email</Label>
                      <FormControl>
                        <Input {...field} id="email" type="email" placeholder="votre@email.com" className="h-8 text-sm" />
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
              </div>
            )}
          </div>
          
          <div className="flex justify-between pt-2 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="h-8 text-xs"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              className="cta-button h-8 text-xs"
              disabled={!selectedDate || !selectedTime || isSubmitting || !form.formState.isValid}
            >
              {isSubmitting ? "Traitement..." : "Confirmer"}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default AppointmentCalendar;
